import { useCallback, useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { FileText, Loader2, Mail, MessageCircle, Plus, Trash2, Wallet } from "lucide-react";
import { toast } from "sonner";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { SearchPicker } from "@/components/SearchPicker";
import { PER_PAGE, Pager } from "@/components/Pager";
import { api, errorMessage, openPdf } from "@/lib/api";
import { openWhatsApp } from "@/lib/whatsapp";
import { formatDate, money, todayISO } from "@/lib/format";
import { StatusBadge } from "@/components/StatusBadge";
import { useAuth } from "@/context/AuthContext";

const PAY_MODES = [
  { value: "neft", label: "NEFT" },
  { value: "rtgs", label: "RTGS" },
  { value: "imps", label: "IMPS" },
  { value: "upi", label: "UPI" },
  { value: "cheque", label: "Cheque" },
  { value: "dd", label: "Demand draft" },
  { value: "cash", label: "Cash" },
  { value: "other", label: "Other" },
];

const BLANK = {
  amount: "",
  paid_on: todayISO(),
  has_tds: false,
  inward_amount: "",
  tds_amount: "",
  mode: "",
  reference: "",
  remitter: "",
  instrument_date: "",
  remark: "",
  show_remittance_on_receipt: false,
};

export default function RunningAccount() {
  const { currency } = useAuth();
  const [scope, setScope] = useState("firm");
  const [targetId, setTargetId] = useState("");
  const [firms, setFirms] = useState([]);
  const [clients, setClients] = useState([]);
  const [account, setAccount] = useState(null);
  const [form, setForm] = useState(BLANK);
  const [remitOpen, setRemitOpen] = useState(false);
  const [busy, setBusy] = useState("");
  const [history, setHistory] = useState([]);
  const [page, setPage] = useState(1);

  useEffect(() => {
    (async () => {
      try {
        const [firmRes, clientRes] = await Promise.all([api.get("/firms"), api.get("/clients")]);
        setFirms(firmRes.data);
        setClients(clientRes.data);
      } catch (error) {
        toast.error(errorMessage(error, "Could not load accounts"));
      }
    })();
  }, []);

  const loadHistory = useCallback(async () => {
    try {
      const { data } = await api.get("/running-accounts/payments");
      setHistory(data);
    } catch (error) {
      toast.error(errorMessage(error, "Could not load past receipts"));
    }
  }, []);

  useEffect(() => {
    loadHistory();
  }, [loadHistory]);

  const amount = form.has_tds
    ? Number(form.inward_amount || 0) + Number(form.tds_amount || 0)
    : Number(form.amount || 0);

  const loadAccount = useCallback(async () => {
    if (!targetId) {
      setAccount(null);
      return;
    }
    try {
      const { data } = await api.get(`/running-accounts/${scope}/${targetId}/open`, {
        params: { amount: amount > 0 ? amount : 0 },
      });
      setAccount(data);
    } catch (error) {
      setAccount(null);
      toast.error(errorMessage(error, "Could not load this account"));
    }
  }, [scope, targetId, amount]);

  useEffect(() => {
    const timer = setTimeout(loadAccount, 250);
    return () => clearTimeout(timer);
  }, [loadAccount]);

  const symbol = account?.currency_symbol || currency;
  const allocation = account?.allocation || [];
  const notes = account?.notes || [];

  const items = useMemo(
    () =>
      scope === "firm"
        ? firms.map((f) => ({ id: f.firm_id, label: f.name, hint: f.city || "" }))
        : clients.map((c) => ({ id: c.client_id, label: c.name, hint: c.city || "" })),
    [scope, firms, clients],
  );

  const submit = async (event) => {
    event.preventDefault();
    if (!targetId) {
      toast.error("Choose the law firm or client the money came from");
      return;
    }
    if (form.has_tds && Number(form.tds_amount || 0) <= 0) {
      toast.error("Enter the TDS deducted, or untick TDS");
      return;
    }
    if (!amount || amount <= 0) {
      toast.error("Enter the amount received");
      return;
    }
    setBusy("save");
    try {
      const { data } = await api.post("/running-accounts/payment", {
        scope,
        target_id: targetId,
        amount,
        paid_on: form.paid_on || todayISO(),
        has_tds: form.has_tds,
        inward_amount: Number(form.inward_amount || 0),
        tds_amount: Number(form.tds_amount || 0),
        mode: form.mode,
        reference: form.reference,
        remitter: form.remitter,
        instrument_date: form.instrument_date || null,
        remark: form.remark,
        show_remittance_on_receipt: form.show_remittance_on_receipt,
      });
      toast.success(
        `${money(data.amount, symbol)} applied across ${data.applied.length} fee note(s) · ${data.settled_count} settled in full`,
      );
      setForm({ ...BLANK, paid_on: form.paid_on });
      setRemitOpen(false);
      await Promise.all([loadAccount(), loadHistory()]);
    } catch (error) {
      toast.error(errorMessage(error, "Could not record the payment"));
    } finally {
      setBusy("");
    }
  };

  const reverse = async (runningId) => {
    if (!window.confirm("Reverse this receipt? Every fee note it settled goes back to unpaid.")) {
      return;
    }
    setBusy(`rev-${runningId}`);
    try {
      await api.delete(`/running-accounts/payments/${runningId}`);
      toast.success("Receipt reversed");
      await Promise.all([loadAccount(), loadHistory()]);
    } catch (error) {
      toast.error(errorMessage(error, "Could not reverse the receipt"));
    } finally {
      setBusy("");
    }
  };

  const send = async (runningId, channel) => {
    setBusy(`${channel}-${runningId}`);
    try {
      if (channel === "email") {
        const { data } = await api.post(
          `/running-accounts/payments/${runningId}/send-email`,
        );
        toast.success(`Receipt emailed to ${data.recipient}`);
      } else {
        const { data } = await api.post(`/running-accounts/payments/${runningId}/whatsapp`);
        openWhatsApp(data);
      }
    } catch (error) {
      toast.error(errorMessage(error, "Could not send the receipt"));
    } finally {
      setBusy("");
    }
  };

  const pagedHistory = history.slice((page - 1) * PER_PAGE, page * PER_PAGE);

  return (
    <div className="space-y-8" data-testid="running-account-page">
      <header>
        <p className="label-caps">Accounts</p>
        <h1 className="mt-3 font-serif text-3xl tracking-tight text-slate-900 lg:text-4xl">
          Running accounts
        </h1>
        <p className="mt-3 max-w-2xl text-sm text-slate-600">
          Record a lump sum received from a law firm or client and it is appropriated across their
          unpaid fee notes, oldest first. You can adjust the split before saving.
        </p>
      </header>

      <div className="grid gap-6 lg:grid-cols-5">
        <form
          className="surface min-w-0 space-y-4 p-6 lg:col-span-2"
          onSubmit={submit}
          data-testid="running-payment-form"
        >
          <div className="space-y-1.5">
            <Label className="label-caps">Money came from</Label>
            <Select
              value={scope}
              onValueChange={(v) => {
                setScope(v);
                setTargetId("");
                setAccount(null);
              }}
            >
              <SelectTrigger className="rounded-sm" data-testid="running-scope-select">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="firm">A law firm</SelectItem>
                <SelectItem value="client">A client</SelectItem>
              </SelectContent>
            </Select>
          </div>

          <div className="space-y-1.5">
            <Label className="label-caps">{scope === "firm" ? "Law firm" : "Client"}</Label>
            <SearchPicker
              items={items}
              value={targetId}
              onSelect={(v) => setTargetId(v || "")}
              placeholder={scope === "firm" ? "Search law firms…" : "Search clients…"}
              testId="running-account-picker"
              emptyLabel="Nothing matches that text."
            />
          </div>

          {form.has_tds ? (
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1.5">
                <Label className="label-caps">Received in bank</Label>
                <Input
                  type="number"
                  step="1"
                  min="0"
                  value={form.inward_amount}
                  onChange={(e) => setForm((f) => ({ ...f, inward_amount: e.target.value }))}
                  className="num rounded-sm"
                  data-testid="running-inward-input"
                />
              </div>
              <div className="space-y-1.5">
                <Label className="label-caps">TDS deducted</Label>
                <Input
                  type="number"
                  step="1"
                  min="0"
                  value={form.tds_amount}
                  onChange={(e) => setForm((f) => ({ ...f, tds_amount: e.target.value }))}
                  className="num rounded-sm"
                  data-testid="running-tds-input"
                />
              </div>
            </div>
          ) : (
            <div className="space-y-1.5">
              <Label className="label-caps">Amount received</Label>
              <Input
                type="number"
                step="1"
                min="0"
                value={form.amount}
                onChange={(e) => setForm((f) => ({ ...f, amount: e.target.value }))}
                placeholder={account ? String(account.outstanding) : ""}
                className="num rounded-sm"
                data-testid="running-amount-input"
              />
            </div>
          )}

          <div className="space-y-1.5">
            <Label className="label-caps">Received on</Label>
            <Input
              type="date"
              value={form.paid_on}
              onChange={(e) => setForm((f) => ({ ...f, paid_on: e.target.value }))}
              className="num rounded-sm"
              data-testid="running-date-input"
            />
          </div>

          <label className="flex items-center gap-2.5 text-[11px] uppercase tracking-[0.12em] text-slate-600">
            <Switch
              checked={!!form.has_tds}
              onCheckedChange={(v) =>
                setForm((f) => ({
                  ...f,
                  has_tds: v,
                  inward_amount: v ? f.inward_amount || f.amount : f.inward_amount,
                }))
              }
              data-testid="running-tds-toggle"
            />
            TDS deducted
          </label>
          {form.has_tds ? (
            <p className="text-[11px] text-slate-600" data-testid="running-tds-credit">
              Credited on account:{" "}
              <span className="num font-semibold text-slate-900">{money(amount, symbol)}</span> ·
              TDS is apportioned across each fee note in the same ratio
            </p>
          ) : null}

          <button
            type="button"
            onClick={() => setRemitOpen((v) => !v)}
            data-testid="running-toggle-remittance"
            className="text-[11px] font-semibold uppercase tracking-[0.12em] text-slate-500 underline decoration-slate-300 transition-colors duration-150 hover:text-slate-900"
          >
            {remitOpen ? "Hide remittance details" : "Add remittance details"}
          </button>

          {remitOpen ? (
            <div className="space-y-3 border border-slate-200 p-3" data-testid="running-remittance-fields">
              <div className="space-y-1.5">
                <Label className="label-caps">Mode</Label>
                <Select
                  value={form.mode || "none"}
                  onValueChange={(v) => setForm((f) => ({ ...f, mode: v === "none" ? "" : v }))}
                >
                  <SelectTrigger className="rounded-sm" data-testid="running-mode-select">
                    <SelectValue placeholder="Not stated" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">Not stated</SelectItem>
                    {PAY_MODES.map((m) => (
                      <SelectItem key={m.value} value={m.value}>
                        {m.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-1.5">
                <Label className="label-caps">Reference / UTR no.</Label>
                <Input
                  value={form.reference}
                  onChange={(e) => setForm((f) => ({ ...f, reference: e.target.value }))}
                  className="num rounded-sm"
                  data-testid="running-reference-input"
                />
              </div>
              <div className="space-y-1.5">
                <Label className="label-caps">Remitter / bank</Label>
                <Input
                  value={form.remitter}
                  onChange={(e) => setForm((f) => ({ ...f, remitter: e.target.value }))}
                  className="rounded-sm"
                  data-testid="running-remitter-input"
                />
              </div>
              <div className="space-y-1.5">
                <Label className="label-caps">Instrument date</Label>
                <Input
                  type="date"
                  value={form.instrument_date}
                  onChange={(e) => setForm((f) => ({ ...f, instrument_date: e.target.value }))}
                  className="num rounded-sm"
                  data-testid="running-instrument-date-input"
                />
              </div>
              <div className="space-y-1.5">
                <Label className="label-caps">Remark</Label>
                <Input
                  value={form.remark}
                  onChange={(e) => setForm((f) => ({ ...f, remark: e.target.value }))}
                  className="rounded-sm"
                  data-testid="running-remark-input"
                />
              </div>
              <label className="flex items-center gap-2.5 text-[11px] uppercase tracking-[0.12em] text-slate-600">
                <Switch
                  checked={!!form.show_remittance_on_receipt}
                  onCheckedChange={(v) =>
                    setForm((f) => ({ ...f, show_remittance_on_receipt: v }))
                  }
                  data-testid="running-print-remittance-toggle"
                />
                Print these on the receipt
              </label>
            </div>
          ) : null}

          <div className="flex flex-wrap gap-2 border-t border-slate-100 pt-4">
            <button
              type="submit"
              disabled={
                busy === "save" ||
                !targetId ||
                amount <= 0 ||
                (!!account && amount > account.outstanding + 0.009)
              }
              data-testid="running-save-button"
              className="inline-flex items-center gap-2 bg-slate-900 px-4 py-2.5 text-[12px] font-semibold text-white transition-colors duration-150 hover:bg-slate-800 disabled:opacity-50"
            >
              {busy === "save" ? (
                <Loader2 className="h-3.5 w-3.5 animate-spin" />
              ) : (
                <Plus className="h-3.5 w-3.5" strokeWidth={2} />
              )}
              Apply to fee notes
            </button>
            {account ? (
              <button
                type="button"
                onClick={() =>
                  setForm((f) =>
                    f.has_tds
                      ? { ...f, inward_amount: String(account.outstanding), tds_amount: "" }
                      : { ...f, amount: String(account.outstanding) },
                  )
                }
                data-testid="running-fill-outstanding"
                className="border border-slate-300 bg-white px-4 py-2.5 text-[12px] font-semibold text-slate-700 transition-colors duration-150 hover:bg-slate-50"
              >
                Full outstanding
              </button>
            ) : null}
          </div>
        </form>

        <div className="min-w-0 space-y-4 lg:col-span-3">
          {!account ? (
            <div className="surface p-10 text-center" data-testid="running-no-account">
              <Wallet className="mx-auto h-6 w-6 text-slate-300" strokeWidth={1.4} />
              <p className="mt-4 text-sm text-slate-600">
                Pick a law firm or client to see what is outstanding on their account.
              </p>
            </div>
          ) : (
            <>
              <div className="surface p-6" data-testid="running-account-summary">
                <div className="flex flex-wrap items-baseline justify-between gap-3">
                  <div>
                    <h2 className="font-serif text-xl text-slate-900">{account.account.name}</h2>
                    <p className="mt-1 text-[12px] text-slate-500">
                      {notes.length} unpaid fee note(s)
                    </p>
                  </div>
                  <p className="num text-xl text-slate-900" data-testid="running-outstanding">
                    {money(account.outstanding, symbol)}
                    <span className="ml-2 text-[11px] uppercase tracking-[0.12em] text-slate-400">
                      outstanding
                    </span>
                  </p>
                </div>

                {amount > 0 && allocation.length > 0 ? (
                  <div className="mt-6" data-testid="running-allocation-preview">
                    <p className="label-caps">
                      How {money(amount, symbol)} will be applied · oldest first
                    </p>
                    <div className="mt-3 max-w-full overflow-x-auto border border-slate-200">
                      <table className="w-full min-w-[560px] text-left">
                        <thead>
                          <tr className="border-b border-slate-200 bg-slate-50">
                            <th className="label-caps px-4 py-2.5">Fee note</th>
                            <th className="label-caps px-4 py-2.5">Raised</th>
                            <th className="label-caps px-4 py-2.5 text-right">Outstanding</th>
                            <th className="label-caps px-4 py-2.5 text-right">Applied</th>
                            <th className="label-caps px-4 py-2.5">Result</th>
                          </tr>
                        </thead>
                        <tbody className="divide-y divide-slate-100">
                          {allocation.map((row) => (
                            <tr
                              key={row.invoice_id}
                              data-testid={`running-alloc-${row.invoice_number}`}
                            >
                              <td className="px-4 py-3">
                                <Link
                                  to={`/invoices/${row.invoice_id}`}
                                  className="num text-[13px] text-slate-900 underline-offset-4 hover:underline"
                                >
                                  {row.invoice_number}
                                </Link>
                                <p className="truncate text-[11px] text-slate-400">
                                  {row.case_title}
                                </p>
                              </td>
                              <td className="num px-4 py-3 text-[12px] text-slate-600">
                                {formatDate(row.issue_date)}
                              </td>
                              <td className="num px-4 py-3 text-right text-[12px] text-slate-600">
                                {money(row.balance, symbol)}
                              </td>
                              <td className="num px-4 py-3 text-right text-[13px] font-semibold text-slate-900">
                                {money(row.applied, symbol)}
                              </td>
                              <td className="px-4 py-3 text-[11px] uppercase tracking-[0.1em]">
                                <span
                                  className={
                                    row.settles ? "text-emerald-700" : "text-amber-700"
                                  }
                                >
                                  {row.settles ? "Settled" : "Part paid"}
                                </span>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                    {account.unallocated > 0 ? (
                      <p
                        className="mt-3 text-[12px] text-red-600"
                        data-testid="running-unallocated"
                      >
                        {money(account.unallocated, symbol)} could not be applied — it is more
                        than this account owes.
                      </p>
                    ) : null}
                  </div>
                ) : (
                  <div className="mt-6 max-w-full overflow-x-auto border border-slate-200">
                    <table className="w-full min-w-[560px] text-left">
                      <thead>
                        <tr className="border-b border-slate-200 bg-slate-50">
                          <th className="label-caps px-4 py-2.5">Fee note</th>
                          <th className="label-caps px-4 py-2.5">Raised</th>
                          <th className="label-caps px-4 py-2.5">Status</th>
                          <th className="label-caps px-4 py-2.5 text-right">Outstanding</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-slate-100">
                        {notes.length === 0 ? (
                          <tr>
                            <td
                              colSpan={4}
                              className="px-4 py-6 text-center text-[13px] text-slate-500"
                            >
                              Nothing outstanding on this account.
                            </td>
                          </tr>
                        ) : (
                          notes.map((row) => (
                            <tr key={row.invoice_id} data-testid={`running-note-${row.invoice_number}`}>
                              <td className="px-4 py-3">
                                <Link
                                  to={`/invoices/${row.invoice_id}`}
                                  className="num text-[13px] text-slate-900 underline-offset-4 hover:underline"
                                >
                                  {row.invoice_number}
                                </Link>
                                <p className="truncate text-[11px] text-slate-400">
                                  {row.case_title}
                                </p>
                              </td>
                              <td className="num px-4 py-3 text-[12px] text-slate-600">
                                {formatDate(row.issue_date)}
                              </td>
                              <td className="px-4 py-3">
                                <StatusBadge status={row.display_status} />
                              </td>
                              <td className="num px-4 py-3 text-right text-[13px] text-slate-900">
                                {money(row.balance, symbol)}
                              </td>
                            </tr>
                          ))
                        )}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
            </>
          )}

          <div className="surface min-w-0" data-testid="running-history">
            <div className="border-b border-slate-200 px-6 py-4">
              <h2 className="font-serif text-xl text-slate-900">Receipts on account</h2>
            </div>
            {history.length === 0 ? (
              <p className="px-6 py-8 text-sm text-slate-500">
                No running-account receipts recorded yet.
              </p>
            ) : (
              <>
                <ul className="divide-y divide-slate-100">
                  {pagedHistory.map((r) => (
                    <li
                      key={r.running_id}
                      className="px-6 py-4"
                      data-testid={`running-history-${r.running_id}`}
                    >
                      <div className="flex flex-wrap items-baseline justify-between gap-2">
                        <div className="min-w-0">
                          <p className="text-[13px] text-slate-900">{r.account_name}</p>
                          <p className="text-[11px] text-slate-400">
                            {formatDate(r.paid_on)} · {r.note_count} fee note(s)
                            {r.has_tds ? " · TDS" : ""}
                            {r.reference ? ` · ${r.reference}` : ""}
                          </p>
                        </div>
                        <p className="num text-[13px] text-slate-900">
                          {money(r.amount, symbol)}
                        </p>
                      </div>
                      <div className="mt-3 flex flex-wrap gap-2">
                        <button
                          type="button"
                          onClick={() =>
                            openPdf(`/running-accounts/payments/${r.running_id}/receipt.pdf`)
                          }
                          data-testid={`running-receipt-pdf-${r.running_id}`}
                          className="inline-flex items-center gap-1.5 border border-slate-200 px-2.5 py-1 text-[10px] font-semibold uppercase tracking-[0.1em] text-slate-600 transition-colors duration-150 hover:border-slate-900 hover:text-slate-900"
                        >
                          <FileText className="h-3 w-3" strokeWidth={1.8} />
                          Receipt
                        </button>
                        <button
                          type="button"
                          onClick={() => send(r.running_id, "email")}
                          disabled={busy === `email-${r.running_id}`}
                          data-testid={`running-receipt-email-${r.running_id}`}
                          className="inline-flex items-center gap-1.5 border border-slate-200 px-2.5 py-1 text-[10px] font-semibold uppercase tracking-[0.1em] text-slate-600 transition-colors duration-150 hover:border-slate-900 hover:text-slate-900 disabled:opacity-60"
                        >
                          {busy === `email-${r.running_id}` ? (
                            <Loader2 className="h-3 w-3 animate-spin" />
                          ) : (
                            <Mail className="h-3 w-3" strokeWidth={1.8} />
                          )}
                          Email
                        </button>
                        <button
                          type="button"
                          onClick={() => send(r.running_id, "whatsapp")}
                          disabled={busy === `whatsapp-${r.running_id}`}
                          data-testid={`running-receipt-whatsapp-${r.running_id}`}
                          className="inline-flex items-center gap-1.5 border border-slate-200 px-2.5 py-1 text-[10px] font-semibold uppercase tracking-[0.1em] text-emerald-700 transition-colors duration-150 hover:border-emerald-600 disabled:opacity-60"
                        >
                          {busy === `whatsapp-${r.running_id}` ? (
                            <Loader2 className="h-3 w-3 animate-spin" />
                          ) : (
                            <MessageCircle className="h-3 w-3" strokeWidth={1.8} />
                          )}
                          WhatsApp
                        </button>
                        <button
                          type="button"
                          onClick={() => reverse(r.running_id)}
                          disabled={busy === `rev-${r.running_id}`}
                          data-testid={`running-reverse-${r.running_id}`}
                          className="inline-flex items-center gap-1.5 border border-slate-200 px-2.5 py-1 text-[10px] font-semibold uppercase tracking-[0.1em] text-slate-500 transition-colors duration-150 hover:border-red-300 hover:text-red-600 disabled:opacity-60"
                        >
                          {busy === `rev-${r.running_id}` ? (
                            <Loader2 className="h-3 w-3 animate-spin" />
                          ) : (
                            <Trash2 className="h-3 w-3" strokeWidth={1.8} />
                          )}
                          Reverse
                        </button>
                      </div>
                    </li>
                  ))}
                </ul>
                <div className="px-6 pb-5">
                  <Pager
                    page={page}
                    total={history.length}
                    onPage={setPage}
                    testId="running-history-pager"
                  />
                </div>
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
